import { z } from "zod";
import {
  PHASE_EXPORT,
  PHASE_PRODUCTION_BUILD,
  PHASE_PRODUCTION_SERVER,
  PHASE_DEVELOPMENT_SERVER,
  PHASE_TEST,
  PHASE_INFO,
} from "next/constants";

const variables = {
  ANALYZE: process.env.ANALYZE,
  ESLINT_IGNORE_DURING_BUILDS: process.env.ESLINT_IGNORE_DURING_BUILDS,
  NEXT_PUBLIC_BACKGROUND_COLOR: process.env.NEXT_PUBLIC_BACKGROUND_COLOR,
  NEXT_PUBLIC_BRAND_NAME: process.env.NEXT_PUBLIC_BRAND_NAME,
  NEXT_PUBLIC_THEME_COLOR: process.env.NEXT_PUBLIC_THEME_COLOR,
  NEXT_PUBLIC_DEFAULT_LOCALE: process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
  NEXT_PUBLIC_SUPPORTED_LOCALES: process.env.NEXT_PUBLIC_SUPPORTED_LOCALES,
  NEXT_PUBLIC_FEATURE_FLAG_HOLDING_REDIRECT_ENABLE:
    process.env.NEXT_PUBLIC_FEATURE_FLAG_HOLDING_REDIRECT_ENABLE,
  NEXT_PUBLIC_FEATURE_FLAG_PWA: process.env.NEXT_PUBLIC_FEATURE_FLAG_PWA,
  NEXT_PUBLIC_FEATURE_FLAG_SEARCH_COLLECTIONS_ENABLE:
    process.env.NEXT_PUBLIC_FEATURE_FLAG_SEARCH_COLLECTIONS_ENABLE,
  NEXT_PUBLIC_FEATURE_FLAG_WISHLIST_ENABLE:
    process.env.NEXT_PUBLIC_FEATURE_FLAG_WISHLIST_ENABLE,
  NEXT_PUBLIC_GENERAL_EMAIL: process.env.NEXT_PUBLIC_GENERAL_EMAIL,
  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID:
    process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
  NEXT_PUBLIC_HYGRAPH_API_TOKEN: process.env.NEXT_PUBLIC_HYGRAPH_API_TOKEN,
  NEXT_PUBLIC_HYGRAPH_PROJECT_ID: process.env.NEXT_PUBLIC_HYGRAPH_PROJECT_ID,
  NEXT_PUBLIC_HYGRAPH_REGION: process.env.NEXT_PUBLIC_HYGRAPH_REGION,
  NEXT_PUBLIC_LEGAL_NAME: process.env.NEXT_PUBLIC_LEGAL_NAME,
  NEXT_PUBLIC_PREVIEW_ACCESS_CODE: process.env.NEXT_PUBLIC_PREVIEW_ACCESS_CODE,
  NEXT_PUBLIC_PROTOCOL: process.env.NEXT_PUBLIC_PROTOCOL,
  NEXT_PUBLIC_SHOPIFY_ADMIN_API_VERSION:
    process.env.NEXT_PUBLIC_SHOPIFY_ADMIN_API_VERSION,
  NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN:
    process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN,
  NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN:
    process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
  NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION:
    process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION,
  NEXT_PUBLIC_SITE_CATEGORIES: process.env.NEXT_PUBLIC_SITE_CATEGORIES,
  NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION:
    process.env.NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION,
  NEXT_PUBLIC_SITE_NAME: process.env.NEXT_PUBLIC_SITE_NAME,
  NEXT_PUBLIC_SOCIAL_CREATOR_X: process.env.NEXT_PUBLIC_SOCIAL_CREATOR_X,
  NEXT_PUBLIC_SOCIAL_URL_FACEBOOK: process.env.NEXT_PUBLIC_SOCIAL_URL_FACEBOOK,
  NEXT_PUBLIC_SOCIAL_URL_INSTAGRAM:
    process.env.NEXT_PUBLIC_SOCIAL_URL_INSTAGRAM,
  NEXT_PUBLIC_SOCIAL_URL_LINKEDIN: process.env.NEXT_PUBLIC_SOCIAL_URL_LINKEDIN,
  NEXT_PUBLIC_SOCIAL_URL_PINTEREST:
    process.env.NEXT_PUBLIC_SOCIAL_URL_PINTEREST,
  NEXT_PUBLIC_SOCIAL_URL_TIKTOK: process.env.NEXT_PUBLIC_SOCIAL_URL_TIKTOK,
  NEXT_PUBLIC_SOCIAL_URL_X: process.env.NEXT_PUBLIC_SOCIAL_URL_X,
  NEXT_PUBLIC_SQUARE_PLACEMENT_MPID:
    process.env.NEXT_PUBLIC_SQUARE_PLACEMENT_MPID,
  NEXT_PUBLIC_SQUARE_PLACEMENT_ID: process.env.NEXT_PUBLIC_SQUARE_PLACEMENT_ID,
  NEXT_PUBLIC_SQUARE_PLACEMENT_SUPPORTED_LOCALES:
    process.env.NEXT_PUBLIC_SQUARE_PLACEMENT_SUPPORTED_LOCALES,
  NEXT_PUBLIC_TAX_ID: process.env.NEXT_PUBLIC_TAX_ID,
  NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
  PHASE: process.env.PHASE, // This is set in next.config.ts based on the phase
  SHOPIFY_ADMIN_API_ACCESS_TOKEN: process.env.SHOPIFY_ADMIN_API_ACCESS_TOKEN,
  SHOPIFY_REVALIDATION_SECRET: process.env.SHOPIFY_REVALIDATION_SECRET,
};

const publicVariablesSchema = z.object({
  NEXT_PUBLIC_BACKGROUND_COLOR: z.string(),
  NEXT_PUBLIC_BRAND_NAME: z.string(),
  NEXT_PUBLIC_DEFAULT_LOCALE: z.string(),
  NEXT_PUBLIC_SUPPORTED_LOCALES: z
    .string()
    .transform((value) => value.split(","))
    .pipe(z.string().array()),
  NEXT_PUBLIC_FEATURE_FLAG_HOLDING_REDIRECT_ENABLE: z.stringbool(),
  NEXT_PUBLIC_FEATURE_FLAG_SEARCH_COLLECTIONS_ENABLE: z.stringbool(),
  NEXT_PUBLIC_FEATURE_FLAG_WISHLIST_ENABLE: z.stringbool(),
  NEXT_PUBLIC_FEATURE_FLAG_PWA: z.stringbool(),
  NEXT_PUBLIC_GENERAL_EMAIL: z.email(),
  // NEXT_PUBLIC_GOOGLE_MY_BUSINESS_ACCOUNT_ID: z.string(),
  // NEXT_PUBLIC_GOOGLE_MY_BUSINESS_LOCATION_ID: z.string(),
  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z.string(),
  NEXT_PUBLIC_HYGRAPH_API_TOKEN: z.string(),
  NEXT_PUBLIC_HYGRAPH_PROJECT_ID: z.string(),
  NEXT_PUBLIC_HYGRAPH_REGION: z.string(),
  NEXT_PUBLIC_LEGAL_NAME: z.string(),
  NEXT_PUBLIC_PREVIEW_ACCESS_CODE: z.string(),
  NEXT_PUBLIC_PROTOCOL: z.union([z.literal("http"), z.literal("https")]),
  NEXT_PUBLIC_SHOPIFY_ADMIN_API_VERSION: z.string(),
  NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN: z.string(),
  NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN: z.string(),
  NEXT_PUBLIC_SHOPIFY_STOREFRONT_API_VERSION: z.string(),
  NEXT_PUBLIC_SITE_DOMAIN_PRODUCTION: z.string(),
  NEXT_PUBLIC_SITE_NAME: z.string(),
  NEXT_PUBLIC_SOCIAL_CREATOR_X: z.string().optional(),
  NEXT_PUBLIC_SOCIAL_URL_FACEBOOK: z.url().optional(),
  NEXT_PUBLIC_SOCIAL_URL_INSTAGRAM: z.url().optional(),
  NEXT_PUBLIC_SOCIAL_URL_LINKEDIN: z.url().optional(),
  NEXT_PUBLIC_SOCIAL_URL_PINTEREST: z.url().optional(),
  NEXT_PUBLIC_SOCIAL_URL_TIKTOK: z.url().optional(),
  NEXT_PUBLIC_SOCIAL_URL_X: z.url().optional(),
  NEXT_PUBLIC_SQUARE_PLACEMENT_MPID: z.string(),
  NEXT_PUBLIC_SQUARE_PLACEMENT_ID: z.string(),
  NEXT_PUBLIC_SQUARE_PLACEMENT_SUPPORTED_LOCALES: z
    .string()
    .transform((value) => value.split(","))
    .pipe(z.string().array()),
  NEXT_PUBLIC_TAX_ID: z.string(),
  NEXT_PUBLIC_THEME_COLOR: z.string(),
  NEXT_PUBLIC_VERCEL_URL: z.string().optional(),
  PHASE: z.enum([
    PHASE_EXPORT,
    PHASE_PRODUCTION_BUILD,
    PHASE_PRODUCTION_SERVER,
    PHASE_DEVELOPMENT_SERVER,
    PHASE_TEST,
    PHASE_INFO,
  ]),
});

const privateVariablesSchemaClient = z.object({
  ANALYZE: z.undefined(),
  ESLINT_IGNORE_DURING_BUILDS: z.undefined(),
  SHOPIFY_ADMIN_API_ACCESS_TOKEN: z.undefined(),
  SHOPIFY_REVALIDATION_SECRET: z.undefined(),
});

const privateVariablesSchemaServer = z.object({
  ANALYZE: z.stringbool().optional(),
  ESLINT_IGNORE_DURING_BUILDS: z.stringbool().optional(),
  SHOPIFY_ADMIN_API_ACCESS_TOKEN: z.string(),
  SHOPIFY_REVALIDATION_SECRET: z.string(),
});

const envSchema = z.intersection(
  publicVariablesSchema,
  typeof window === "undefined"
    ? privateVariablesSchemaServer
    : privateVariablesSchemaClient,
);

export const env = envSchema.parse(variables);
